import * as React from 'react';
import { RefObject, useRef } from 'react';

import { use$ } from '@legendapp/state/react';
import { Breakpoints, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import styled, { css } from 'styled-components';

import { landscapeMediaMixin } from '../../hooks/use-vertical-breakpoint';

import { SliderMagicMixin } from './option-selection-wrapper';

import translate from '../../utils/translate';

import { ContentId, ProductView, TRACKING_TIMER, TrackingEventName } from '../../store/stores/tracking/types';

import {
	degreeImageStore,
	fullscreenStore,
	interiorStore,
	interiorStoreActions,
	rootStore,
	rootStoreActions,
	sliderStore
} from '../../store';
import { trackingStore } from '../../store/stores/tracking-store';
import { Option } from './option';
import { PickerType } from './picker';
import Slider from './slider';

interface OptionConfig {
	type: PickerType;
	data: OptionData[];
	onSelect: (code: string, ref?: RefObject<HTMLButtonElement>) => void;
	getCurrentActive: () => string;
}

interface OptionData {
	code: string;
	name: string;
	assets: string;
	isActive?: boolean;
}

export const OptionSelection = () => {
	const optionRef = useRef<HTMLDivElement>(null);
	const optionRefs = useRef<Map<number, RefObject<HTMLButtonElement>>>(new Map());

	const activePicker = use$(sliderStore.activePicker);
	const isDegreeLoading = use$(degreeImageStore.isImageLoaded);

	const isInteriorMode = use$(interiorStore.isInteriorLoaded);
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);
	const isDoubleSliderVisible = use$(sliderStore.isDoubleSliderVisible);
	const interiorLabel = translate('global.interiorOptionLabel');

	const optionConfigs: OptionConfig[] = [
		{
			type: PickerType.Color,
			data: getColorOptions(),
			onSelect: code => {
				handleColorSelection(code);
			},
			getCurrentActive: () => getCurrentColorCode()
		},

		{
			type: PickerType.Rim,
			data: getRimOptions(),
			onSelect: code => {
				handleRimSelection(code);
			},
			getCurrentActive: () => getCurrentRimCode()
		},
		{
			type: PickerType.Interior,
			data: getInteriorOptions(),
			onSelect: code => {
				handleInteriorSelection(code);
			},
			getCurrentActive: () => getCurrentInteriorCode()
		}
	];

	const activeConfig = optionConfigs.find(config =>
		isInteriorMode ? config.type === PickerType.Interior : config.type === activePicker
	);

	if (!activeConfig) return null;

	const getOptionRef = (index: number): RefObject<HTMLButtonElement> => {
		if (!optionRefs.current.has(index)) {
			optionRefs.current.set(index, { current: null });
		}
		return optionRefs.current.get(index)!;
	};

	const options = activeConfig.data.map((item, index) => {
		const isActive = item.code === activeConfig.getCurrentActive();
		const optionsRef = getOptionRef(index);
		const displayName = activeConfig.type === PickerType.Interior ? interiorLabel : item.name || '';
		const baseAriaLabel = `${isActive ? 'Active' : 'Inactive'} ${activeConfig.type}`;
		const ariaLabel = displayName ? `${baseAriaLabel}: ${displayName}` : baseAriaLabel;

		return {
			key: item.code,
			ref: optionsRef,
			ariaBusy: isDegreeLoading,
			ariaLabel,
			testId: `${activeConfig.type}_Option_${item.code}`,
			thumbnailUrl: item.assets,
			altText: displayName,
			onClick: () => activeConfig.onSelect(item.code, optionsRef),
			selectedOption: isActive
		};
	});

	// @TODO this is baaaad! it happens on every render
	let activeOptionRef: RefObject<HTMLButtonElement> | undefined;
	let activeOptionText = '';
	if (activeConfig) {
		const activeItemCode = activeConfig.getCurrentActive();
		const activeItemIndex = activeConfig.data.findIndex(item => item.code === activeItemCode);

		activeOptionText =
			activeConfig.type === PickerType.Interior ? interiorLabel : options[activeItemIndex]?.altText || '';
		if (activeItemIndex !== -1) {
			activeOptionRef = optionRefs.current.get(activeItemIndex);
		}
	}

	return (
		<OptionsLabelWrapper $isDoubleSliderVisible={isDoubleSliderVisible}>
			<OptionsWrapper ref={optionRef} $isFullscreenMode={isFullscreenMode}>
				<Slider parentRef={optionRef} activeOptionOrPickerRef={activeOptionRef} showArrows>
					{options.map(({ key, ...optionProps }) => (
						<Option key={key} {...optionProps} />
					))}
				</Slider>
			</OptionsWrapper>
			<OptionLabel staticSize appearance={TokenTextAppearance.headline100} $isFullscreenMode={isFullscreenMode}>
				{activeOptionText || <>&nbsp;</>}
			</OptionLabel>
		</OptionsLabelWrapper>
	);
};

// @TODO this is baaad!
const getColorOptions = (): OptionData[] => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	const [currentVariantKey] = use$(rootStore.currentVariant);
	const [colorKey, rimKey] = currentVariantKey.split('-');

	const existingExterior = currentModelValue.exterior
		? Object.keys(currentModelValue.exterior)
				.filter(key => key.split('-')[1] === rimKey)
				.map(key => key.split('-')[0])
		: [];

	const existingExteriorSet = new Set(existingExterior);

	return Object.entries(currentModelValue?.thumbnailColor || {})
		.filter(([code]) => existingExteriorSet.has(code))
		.map(([code, data]) => ({
			code,
			name: data.name,
			assets: data.assets,
			isActive: code === colorKey
		}));
};

const getRimOptions = (): OptionData[] => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	const [currentVariantKey] = use$(rootStore.currentVariant);
	const [colorKey, rimKey] = currentVariantKey.split('-');

	const existingExterior = currentModelValue.exterior
		? Object.keys(currentModelValue.exterior)
				.filter(key => key.split('-')[0] === colorKey)
				.map(key => key.split('-')[1])
		: [];

	const existingExteriorSet = new Set(existingExterior);

	return Object.entries(currentModelValue?.thumbnailRim || {})
		.filter(([code]) => existingExteriorSet.has(code))
		.map(([code, data]) => ({
			code,
			name: data.name,
			assets: data.assets,
			isActive: code === rimKey
		}));
};

const getInteriorOptions = (): OptionData[] => {
	const [_, currentModelValue] = use$(rootStore.currentModel);
	const [currentInteriorKey] = use$(interiorStore.currentInterior);

	return Object.entries(currentModelValue?.thumbnailInterior || {}).map(([code, data]) => ({
		code,
		name: data.name,
		assets: data.assets,
		isActive: code === currentInteriorKey
	}));
};

const getVariantAssets = (variantKey: string): [string[], string] => {
	const [_, currentModelValue] = rootStore.currentModel.get();

	const exteriorObj = currentModelValue.exterior?.[variantKey] || ({} as { assets: string[]; baseUrl: string });

	return [exteriorObj.assets || [], exteriorObj.baseUrl || ''];
};

const handleColorSelection = (colorCode: string) => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [, rimKey] = currentVariantKey.split('-');
	const newVariantKey = `${colorCode}-${rimKey}`;

	const [assets, baseUrl] = getVariantAssets(newVariantKey);

	rootStoreActions.setVariantData([newVariantKey, assets, baseUrl]);

	trackingStore.setTrackingTimer(TRACKING_TIMER.COLOR_SELECTION_NAME, TrackingEventName.VISUALIZER_COLOR_CLICK, {
		contentId: ContentId.MENU,
		sectionName: ProductView.EXTERNAL_VIEW
	});
};

const handleRimSelection = (rimCode: string) => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [colorKey] = currentVariantKey.split('-');
	const newVariantKey = `${colorKey}-${rimCode}`;

	const [assets, baseUrl] = getVariantAssets(newVariantKey);

	rootStoreActions.setVariantData([newVariantKey, assets, baseUrl]);

	trackingStore.setTrackingTimer(TRACKING_TIMER.RIM_SELECTION_NAME, TrackingEventName.VISUALIZER_WHEELS_CLICK, {
		contentId: ContentId.MENU,
		sectionName: ProductView.EXTERNAL_VIEW
	});
};

const handleInteriorSelection = (interiorCode: string) => {
	const [_, currentModelValue] = rootStore.currentModel.get();
	if (currentModelValue.interior && currentModelValue.interior[interiorCode]) {
		interiorStoreActions.setInteriorData([interiorCode, currentModelValue.interior[interiorCode].assets]);
	}

	trackingStore.setTrackingTimer(
		TRACKING_TIMER.PRODUCTVIEW_INTERIOR_NAME,
		TrackingEventName.VISUALIZER_INTERIOR_CLICK,
		{
			sectionName: ProductView.INTERIOR_VIEW,
			contentId: ContentId.MENU
		}
	);
};

const getCurrentColorCode = (): string => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [colorKey] = currentVariantKey.split('-');
	return colorKey || '';
};

const getCurrentRimCode = (): string => {
	const [currentVariantKey] = rootStore.currentVariant.get();
	const [, rimKey] = currentVariantKey.split('-');
	return rimKey || '';
};

const getCurrentInteriorCode = (): string => {
	const [currentInteriorKey] = interiorStore.currentInterior.get();
	return currentInteriorKey || '';
};

const OptionsLabelWrapper = styled.div<{ $isDoubleSliderVisible: boolean }>`
	${props => SliderMagicMixin(props.$isDoubleSliderVisible)}

	overflow: hidden;
`;

const OptionLabel = styled(Text)<{ $isFullscreenMode: boolean }>`
	// Needs width of container as rest overflows in long slider
	width: 100%;
	margin: 0 auto;

	// Padding spaces between options above and chips below, additional left on mobile
	padding: ${({ theme }) =>
		`${theme.size.static100} ${theme.size.static300} ${theme.size.static150} ${theme.size.static300}`};

	text-align: start;

	@media (min-width: ${Breakpoints.b960}px) {
		text-align: center;
		padding: ${({ theme }) => `${theme.size.static100} 0 ${theme.size.static150} 0`};
	}

	@media ${landscapeMediaMixin} {
		display: none;
	}

	${({ $isFullscreenMode }) => $isFullscreenMode && 'display:none;'}
	${({ theme }) =>
		theme.variant === 'v26' &&
		css`
			&& {
				color: #ffffff;
				font-weight: 400;
				font-size: 14px;
				line-height: 120%;
			}
		`}
`;

const OptionsWrapper = styled.div<{ $isFullscreenMode: boolean }>`
	// Slider Specific
	// Similar styling as in options-selector-wrapper picker
	display: flex;
	justify-content: start;
	// margin-bottom: ${({ theme }) => theme.size.static300};

	${({ $isFullscreenMode }) => $isFullscreenMode && 'justify-content: end;'} @media ${landscapeMediaMixin} {
		justify-content: end;
	}

	@media (min-width: ${Breakpoints.b960}px) {
		justify-content: center;
		${({ $isFullscreenMode }) => $isFullscreenMode && 'justify-content: end;'}
	}
`;
